import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { FlexColumnContainer, StrongBoldCss } from "@trailhead/core-ui";
import { useDeviceState } from "@trailhead/global-device-context";
import { trackEvent } from "@trailhead/analytics";
import {
  Button as AsurionButton,
  Dropdown,
  DropdownOptions,
  Text,
} from "@trailhead/asurion-ui";
import {
  FALLBACK_DEVICE_DROPDOWN_INPUT_LABELS,
  FALLBACK_DEVICE_DROPDOWN_LABELS,
  FALLBACK_DEVICE_LIST,
  FALLBACK_DEVICE_QUESTION_LABELS,
  FALLBACK_DEVICE_QUESTION_LABELS_ATT,
  FlowTypes,
  MobileDeviceValue,
  MobileDeviceValues,
  useQueryParams,
} from "@trailhead/smart-setup-core";

import { useSanitizedTranslation } from "@trailhead/i18n";
import parse from "html-react-parser";
import dompurify from "dompurify";
import { useMxContext } from "@trailhead/mx-context";
import { PartnerType } from "@trailhead/contentful";

const DeviceSelectorsContainer = styled(FlexColumnContainer)`
  align-items: space-between;
  ${StrongBoldCss}
`;

const SelectorContainer = styled.div<{ hasMarginTop?: boolean }>`
  margin: ${props => (props.hasMarginTop ? 32 : 0)}px 0px 32px 0px;
`;

const InputLabel = styled(Text)`
  font-size: 24px;
  margin-bottom: 1.5rem;
  display: inline-block;
`;

const ButtonSection = styled.div`
  display: flex;
  margin-top: 18px;
  & > * {
    margin-right: 15px;
  }
`;

const AsurionButtonWrapper = styled(AsurionButton)`
  width: 40%;
  height: 45px;
  padding: 12px 18px;
`;

const AccessQuestion = styled(Text)``;

export const DeviceSelectors: React.FC = () => {
  const t = useSanitizedTranslation();
  const noString = t("No");
  const yesString = t("Yes");
  const queryParams = useQueryParams();
  const { client } = useMxContext();
  const cid = queryParams.get("cid");

  const {
    oldDeviceFunctional,
    newDeviceObName,
    oldDeviceObName,
    oldDeviceAccess,
    newDeviceActivated,
    deviceFlowType,
    newDeviceIsiPhone14,
    updateOldDeviceFunctional,
    updateOldDeviceAccess,
    updateOldDeviceObName,
    updateNewDeviceObName,
    updateNewDeviceActivated,
    updateDeviceFlowType,
    updateNewDeviceIsiPhone14,
  } = useDeviceState();

  const [newDevice, setNewDevice] = useState<string>(newDeviceObName ?? "");
  const [oldDevice, setOldDevice] = useState<string>(oldDeviceObName ?? "");

  const deviceLists = FALLBACK_DEVICE_LIST;

  const deviceDropdownLabels = client === PartnerType.ATT ? 
    {
      newDeviceDropdownLabel: "What make is your replacement device?",
      oldDeviceDropdownLabel: "What make is your original device?"
    }
    : FALLBACK_DEVICE_DROPDOWN_LABELS;

  const deviceDropdownInputLabels = client === PartnerType.ATT ? 
    {
      newDeviceInputLabel: "Choose your replacement device",
      oldDeviceInputLabel: "Choose your original device"
    }
    : FALLBACK_DEVICE_DROPDOWN_INPUT_LABELS;

  const questionLabels = client === PartnerType.ATT ? FALLBACK_DEVICE_QUESTION_LABELS_ATT: FALLBACK_DEVICE_QUESTION_LABELS;

  const isNextStepOverrideEnabled = true;

  const showiPhone14Prompt = [PartnerType.VERIZON,PartnerType.LIBERTY, PartnerType.US_CELLULAR].includes(client as unknown as PartnerType);

  const oldDeviceNameNotAvailable = "N/A";

  const onUpdateOldDeviceFunctionalChange = (value: boolean) => {
    if (!deviceFlowType && !isNextStepOverrideEnabled) {
      updateDeviceFlowType(FlowTypes.Replacement);
    }

    trackEvent("Device_Functional_Selected_Click", {
      isOldDeviceFunctional: value,
    });

    updateOldDeviceFunctional(value);
  };

  const onUpdateOldDeviceAccess = (value: boolean) => {
    trackEvent("Device_Access_Selected_Click", {
      hasOldDeviceAccess: value,
    });
    if (value) {
      updateOldDeviceAccess(true);
    } else {
      updateOldDeviceAccess(false);
      updateOldDeviceFunctional(false);
    }
  };

  const onUpdateNewDeviceActivated = (value: boolean) => {
    trackEvent("Device_Activated_Selected_Click", {
      newDeviceActivated: value,
    });
    if (value) {
      updateNewDeviceActivated(true);
    } else {
      updateNewDeviceActivated(false);
    }
  };

  const onUpdateFlowType = (value: FlowTypes) => {
    trackEvent("Device_FlowType_Selected_Click", {
      deviceFlowType: value.toString(),
    });
    updateDeviceFlowType(value);
  };

  const onUpdateNewDeviceIsiPhone14 = (value: boolean) => {
    trackEvent("Device_Is_iPhone14_Click", {
      isIphone14: value,
    });
    updateNewDeviceIsiPhone14(value);
  };

  const handleNewDeviceUpdate = useCallback(
    (device: string) => {
      setNewDevice(device);
      // TODO we need to fix this. temporary janky solution
      updateNewDeviceObName(
        (device as MobileDeviceValue) === MobileDeviceValues.Pixel
          ? MobileDeviceValues.OtherAndroid
          : (device as MobileDeviceValue),
      );
      trackEvent("Replacement_Device_Selected_Click", {
        newDevice: device,
      });
    },
    [updateNewDeviceObName, setNewDevice],
  );

  const handleOldDeviceUpdate = useCallback(
    (device: string) => {
      setOldDevice(device);

      updateOldDeviceObName(
        (device as MobileDeviceValue) === MobileDeviceValues.Pixel
          ? MobileDeviceValues.OtherAndroid
          : (device as MobileDeviceValue),
      );
      trackEvent("Old_Device_Selected_Click", { oldDevice: device });

      if (device === oldDeviceNameNotAvailable) {
        updateOldDeviceAccess(false);
        updateOldDeviceFunctional(false);
      }
    },
    [
      updateOldDeviceObName,
      updateOldDeviceAccess,
      updateOldDeviceFunctional,
      setOldDevice,
    ],
  );

  useEffect(() => {
    if (deviceLists.newDeviceList.length === 1 && !newDeviceObName) {
      handleNewDeviceUpdate(deviceLists.newDeviceList[0].value);
    }
    if (deviceLists.oldDeviceList.length === 1 && !oldDeviceObName) {
      handleOldDeviceUpdate(deviceLists.oldDeviceList[0].value);
    }
  }, [
    deviceLists,
    newDeviceObName,
    oldDeviceObName,
    handleOldDeviceUpdate,
    handleNewDeviceUpdate,
  ]);

  //Temporary measure, once the new verizon smart setup
  //is deployed, line 232 - 239 can be removed.
  //TODO: Remove lines above^ and update the oldDeviceList
  //variable back to deviceList.oldDeviceList
  const isVerizonSmartSetupBackup =
    client === PartnerType.VERIZON && cid === "vz_bu_ss";
  let oldDeviceList = deviceLists.oldDeviceList;
  if (isVerizonSmartSetupBackup) {
    oldDeviceList = oldDeviceList.filter(
      ({ name }) => name !== oldDeviceNameNotAvailable,
    );
  }

  deviceLists.newDeviceList.map((item: DropdownOptions) => {
    item.name = t(item.name);
    return item;
  });
  oldDeviceList.map((item: DropdownOptions) => {
    item.name = t(item.name);
    return item;
  });

  const skipNonDeviceSelectionQuestions =
    queryParams.get("skipQuestions") === "true";
  useEffect(() => {
    if (skipNonDeviceSelectionQuestions) {
      updateDeviceFlowType(FlowTypes.NewDevice);
      updateNewDeviceActivated(true);
      updateOldDeviceAccess(true);
      updateOldDeviceFunctional(true);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [skipActivatedQuestion, updateSkipActivatedQuestion] =
    useState<boolean>(false);
  useEffect(() => {
    const shouldSkip =
      client === PartnerType.VERIZON && deviceFlowType === FlowTypes.NewDevice;
    updateSkipActivatedQuestion(shouldSkip);
    if (shouldSkip) {
      updateNewDeviceActivated(false);
    }
  }, [deviceFlowType, client, updateNewDeviceActivated]);

  return (
    <DeviceSelectorsContainer>
      <SelectorContainer hasMarginTop={true} data-testid="new-device-dropdown">
        <InputLabel>
          {" "}
          {parse(
            dompurify.sanitize(t(deviceDropdownLabels.newDeviceDropdownLabel)),
          )}{" "}
        </InputLabel>
        <Dropdown
          id="NewDeviceDropdown"
          onChange={(
            event:
              | React.ChangeEvent<HTMLSelectElement>
              | React.ChangeEvent<HTMLInputElement>,
          ) => handleNewDeviceUpdate(event.currentTarget.value)}
          options={deviceLists.newDeviceList}
          label={t(deviceDropdownInputLabels.newDeviceInputLabel)}
          value={newDevice}
        />
      </SelectorContainer>

      <SelectorContainer data-testid="old-device-dropdown">
        <InputLabel>
          {parse(
            dompurify.sanitize(t(deviceDropdownLabels.oldDeviceDropdownLabel)),
          )}
        </InputLabel>
        <Dropdown
          id="OldDeviceDropdown"
          onChange={(
            event:
              | React.ChangeEvent<HTMLSelectElement>
              | React.ChangeEvent<HTMLInputElement>,
          ) => handleOldDeviceUpdate(event.currentTarget.value)}
          options={oldDeviceList}
          label={t(deviceDropdownInputLabels.oldDeviceInputLabel)}
          value={oldDevice}
        />
      </SelectorContainer>
      {showiPhone14Prompt &&
        oldDeviceObName &&
        newDeviceObName === MobileDeviceValues.iPhone &&
        isNextStepOverrideEnabled && (
          <SelectorContainer data-testid="is-new-device-iphone14">
            <AccessQuestion size={4}>
              {parse(
                dompurify.sanitize(
                  t(questionLabels.newDeviceIsNewiPhoneVersion),
                ),
              )}
            </AccessQuestion>
            <ButtonSection>
              <AsurionButtonWrapper
                onClick={() => onUpdateNewDeviceIsiPhone14(true)}
                size="medium"
                variant={
                  newDeviceIsiPhone14 === null || !newDeviceIsiPhone14
                    ? "outline"
                    : "default"
                }
                data-testid="is-new-device-iphone14-button-yes"
              >
                {yesString}
              </AsurionButtonWrapper>
              <AsurionButtonWrapper
                onClick={() => onUpdateNewDeviceIsiPhone14(false)}
                size="medium"
                variant={
                  newDeviceIsiPhone14 === null || newDeviceIsiPhone14
                    ? "outline"
                    : "default"
                }
                data-testid="is-new-device-iphone14-button-no"
              >
                {noString}
              </AsurionButtonWrapper>
            </ButtonSection>
          </SelectorContainer>
        )}
      {!skipNonDeviceSelectionQuestions &&
        oldDeviceObName &&
        newDeviceObName &&
        isNextStepOverrideEnabled && (
          <SelectorContainer data-testid="is-device-replacement">
            <AccessQuestion size={4}>
              {parse(
                dompurify.sanitize(t(questionLabels.replacementThroughClaim)),
              )}
            </AccessQuestion>
            <ButtonSection>
              <AsurionButtonWrapper
                onClick={() => onUpdateFlowType(FlowTypes.Replacement)}
                size="medium"
                variant={
                  deviceFlowType === null ||
                  deviceFlowType === FlowTypes.NewDevice
                    ? "outline"
                    : "default"
                }
                data-testid="flow-type-button-new"
              >
                {yesString}
              </AsurionButtonWrapper>
              <AsurionButtonWrapper
                onClick={() => onUpdateFlowType(FlowTypes.NewDevice)}
                size="medium"
                variant={
                  deviceFlowType === null ||
                  deviceFlowType === FlowTypes.Replacement
                    ? "outline"
                    : "default"
                }
                data-testid="flow-type-button-old"
              >
                {noString}
              </AsurionButtonWrapper>
            </ButtonSection>
          </SelectorContainer>
        )}

      {!skipNonDeviceSelectionQuestions &&
        deviceFlowType &&
        oldDeviceObName &&
        newDeviceObName &&
        !skipActivatedQuestion &&
        isNextStepOverrideEnabled && (
          <SelectorContainer data-testid="is-device-activated">
            <AccessQuestion size={4}>
              {parse(
                dompurify.sanitize(t(questionLabels.newDeviceAlreadyActivated)),
              )}
            </AccessQuestion>
            <ButtonSection>
              <AsurionButtonWrapper
                onClick={() => onUpdateNewDeviceActivated(true)}
                size="medium"
                variant={
                  newDeviceActivated === null || !newDeviceActivated
                    ? "outline"
                    : "default"
                }
                data-testid="new-device-activated-button-yes"
              >
                {yesString}
              </AsurionButtonWrapper>
              <AsurionButtonWrapper
                onClick={() => onUpdateNewDeviceActivated(false)}
                size="medium"
                variant={
                  newDeviceActivated === null || newDeviceActivated
                    ? "outline"
                    : "default"
                }
                data-testid="new-device-activated-button-no"
              >
                {noString}
              </AsurionButtonWrapper>
            </ButtonSection>
          </SelectorContainer>
        )}

      {!skipNonDeviceSelectionQuestions &&
        oldDevice !== oldDeviceNameNotAvailable &&
        oldDeviceObName &&
        newDeviceObName &&
        (newDeviceActivated != null || !isNextStepOverrideEnabled) && (
          <>
            <SelectorContainer data-testid="old-device-access">
              <AccessQuestion size={4}>
                {parse(dompurify.sanitize(t(questionLabels.oldDeviceAccess)))}
              </AccessQuestion>
              <ButtonSection>
                <AsurionButtonWrapper
                  onClick={() => onUpdateOldDeviceAccess(true)}
                  size="medium"
                  variant={
                    oldDeviceAccess === null || !oldDeviceAccess
                      ? "outline"
                      : "default"
                  }
                  data-testid="old-device-access-button-yes"
                >
                  {yesString}
                </AsurionButtonWrapper>
                <AsurionButtonWrapper
                  onClick={() => onUpdateOldDeviceAccess(false)}
                  size="medium"
                  variant={
                    oldDeviceAccess === null || oldDeviceAccess
                      ? "outline"
                      : "default"
                  }
                  data-testid="old-device-access-button-no"
                >
                  {noString}
                </AsurionButtonWrapper>
              </ButtonSection>
            </SelectorContainer>
          </>
        )}
      {!skipNonDeviceSelectionQuestions && oldDeviceAccess === true && (
        <>
          <SelectorContainer data-testid="old-device-functional">
            <AccessQuestion size={4}>
              {parse(dompurify.sanitize(t(questionLabels.oldDeviceFunctional)))}
            </AccessQuestion>
            <ButtonSection>
              <AsurionButtonWrapper
                onClick={() => onUpdateOldDeviceFunctionalChange(true)}
                size="medium"
                variant={
                  oldDeviceFunctional === null || !oldDeviceFunctional
                    ? "outline"
                    : "default"
                }
                data-testid="old-device-functional-button-yes"
              >
                {yesString}
              </AsurionButtonWrapper>
              <AsurionButtonWrapper
                onClick={() => onUpdateOldDeviceFunctionalChange(false)}
                size="medium"
                variant={
                  oldDeviceFunctional === null || oldDeviceFunctional
                    ? "outline"
                    : "default"
                }
                data-testid="old-device-functional-button-no"
              >
                {noString}
              </AsurionButtonWrapper>
            </ButtonSection>
          </SelectorContainer>
        </>
      )}
    </DeviceSelectorsContainer>
  );
};
